import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Section, TextField, TextArea, Toggle, AdminButton } from "@/components/admin/AdminUI";
import MultiMediaUploader from "@/components/admin/MultiMediaUploader";
import { Pencil, Trash2, Plus } from "lucide-react";

const empty = {
  title: "", location: "", short_story: "", design_direction: "", favourite_detail: "",
  images: [], featured: false, display_order: 0,
};

export default function StoriesAdmin() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/stories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      if (editing.id) await api.put(`/stories/${editing.id}`, editing);
      else await api.post("/stories", editing);
      setEditing(null);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    await api.delete(`/stories/${s.id}`);
    load();
  };

  return (
    <AdminLayout>
      <Section title="Wedding Stories" actions={<AdminButton testId="new-story" onClick={() => setEditing({ ...empty })}><Plus size={14} /> New Story</AdminButton>}>
        <div className="border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface)]">
              <tr className="text-left eyebrow text-[color:var(--text-muted)]">
                <th className="p-4">Title</th>
                <th className="p-4 hidden md:table-cell">Location</th>
                <th className="p-4">Images</th>
                <th className="p-4">Featured</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} data-testid={`row-story-${s.id}`} className="border-t border-[color:var(--border)]">
                  <td className="p-4 font-display italic text-lg">{s.title}</td>
                  <td className="p-4 hidden md:table-cell text-[color:var(--text-muted)]">{s.location}</td>
                  <td className="p-4">{(s.images || []).length}</td>
                  <td className="p-4">{s.featured ? "★" : ""}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`edit-story-${s.id}`} onClick={() => setEditing({ ...empty, ...s })} className="mr-3 hover:text-[color:var(--accent)]"><Pencil size={16} /></button>
                    <button data-testid={`delete-story-${s.id}`} onClick={() => del(s)} className="hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-[color:var(--text-muted)]">No stories yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4 md:p-10" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <form onSubmit={save} className="max-w-2xl mx-auto bg-[color:var(--surface)] border border-[color:var(--border)] p-6 md:p-10">
            <h3 className="font-display italic text-3xl mb-6">{editing.id ? "Edit Story" : "New Story"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <TextField testId="story-title" label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextField testId="story-location" label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
            </div>
            <div className="mb-4"><TextArea testId="story-short" label="Short story" value={editing.short_story} onChange={(v) => setEditing({ ...editing, short_story: v })} rows={3} /></div>
            <div className="mb-4"><TextField testId="story-direction" label="Design direction" value={editing.design_direction} onChange={(v) => setEditing({ ...editing, design_direction: v })} /></div>
            <div className="mb-4"><TextField testId="story-fav" label="Favourite detail" value={editing.favourite_detail} onChange={(v) => setEditing({ ...editing, favourite_detail: v })} /></div>
            <div className="mb-6">
              <div className="eyebrow mb-2">Selected images</div>
              <MultiMediaUploader testId="story-images-upload" items={editing.images} onChange={(items) => setEditing({ ...editing, images: items })} folder="stories" purpose="story_image" resourceType="image" />
            </div>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <TextField testId="story-order" label="Display order" type="number" value={editing.display_order} onChange={(v) => setEditing({ ...editing, display_order: parseInt(v) || 0 })} />
              <Toggle testId="story-featured" label="Featured" value={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} />
            </div>
            {err && <div className="text-red-400 text-sm mb-4">{err}</div>}
            <div className="flex justify-end gap-3">
              <AdminButton variant="ghost" onClick={() => setEditing(null)}>Cancel</AdminButton>
              <AdminButton type="submit" testId="story-save" disabled={saving}>{saving ? "Saving…" : "Save"}</AdminButton>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
